import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { StateConfig } from '@app/core/_config/states.config';
import { ClientShippingAddressService } from '@app/core/client/services/client-shipping-address.service';
import { Client, ClientShippingAddress } from '@app/core/client/store/client.model';
import { LocationService } from '@app/core/locations/services/location.service';

@Component({
  selector: 'cnc-shipping-address-popup-form',
  templateUrl: './shipping-address-popup-form.component.html',
  styleUrls: ['./shipping-address-popup-form.component.scss'],
})
export class ShippingAddressPopupFormComponent implements OnInit, OnDestroy {
  formData :UntypedFormGroup;
  pageTitle :string = 'New shipping address';
  loading :boolean;
  submitted :boolean;
  countries :{ key :string; value :string }[];
  isSameAsBusinessAddress :boolean;

  @Input() shippingAddress :ClientShippingAddress;
  @Input() businessAccount :Client;
  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private clientShippingAddressService :ClientShippingAddressService,
    public activeModal :NgbActiveModal,
    private toastr :ToastrService,
    public locationSvc :LocationService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    if (this.shippingAddress) {
      this.formData.patchValue(this.shippingAddress);
      this.pageTitle = 'Update shipping address';
    }
  }

  private initCountries() :void {
    this.locationSvc
      .getCountryList()
      .pipe(
        catchError((response) => {
          this.toastr.error(response.error || 'Error on load countries', 'Error');

          return throwError(response.error);
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe((resp) => {
        this.countries = resp;
      });
  }

  initForm() {
    this.formData = this.fb.group({
      address: ['', Validators.required],
      addressLine2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      postalCode: ['', Validators.required],
      country: ['', Validators.required],
      companyName: [this.businessAccount?.business_name, Validators.required],
      accountNumber: [''],
      firstName: [''],
      lastName: [''],
      phoneNumber: [''],
      email: [
        '',
        [Validators.email, Validators.minLength(3), Validators.maxLength(320)],
      ],
      shippingType: [''],
      notes: [''],
    });
    this.initCountries();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onCountryChange(country :string) :void {
    this.formData.controls.country.setValue(country);
  }

  onSubmit() {
    const payload :ClientShippingAddress = this.formData.value;
    this.loading = true;
    this.submitted = true;
    if (this.shippingAddress && this.shippingAddress.id) {
      this.clientShippingAddressService
        .update(this.businessAccount.id, this.shippingAddress.id, payload)
        .pipe(
          catchError((response) => {
            this.toastr.error(
              response.error || 'Update error. Try again later',
              'Error',
            );
            this.loading = false;

            return throwError(response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe((clientShippingAddress :ClientShippingAddress) => {
          this.toastr.success('Updated successfully.', 'Success');
          this.activeModal.close(clientShippingAddress);
          this.loading = false;
        });
    } else {
      this.clientShippingAddressService
        .create(this.businessAccount.id, payload)
        .pipe(
          catchError((response) => {
            this.toastr.error(
              response.error || 'Save error. Try again later',
              'Error',
            );
            this.loading = false;

            return throwError(response.error);
          }),
          takeUntil(this.onDestroy$),
        )
        .subscribe((clientShippingAddress :ClientShippingAddress) => {
          this.toastr.success('Created successfully.', 'Success');
          this.activeModal.close(clientShippingAddress);
          this.loading = false;
        });
    }
  }

  showOptions(event :MatCheckboxChange) :void {
    event.checked ? this.populateFromBusinessAddress() : this.resetAddress();
    this.isSameAsBusinessAddress = event.checked;
  }

  private populateFromBusinessAddress() :void {
    this.formData.get('address').patchValue(this.businessAccount.business_address);
    this.formData
      .get('addressLine2')
      .patchValue(this.businessAccount.business_address_line_2);
    this.formData.get('state').patchValue(this.businessAccount.business_state);
    this.formData.get('city').patchValue(this.businessAccount.business_city);
    this.formData.get('postalCode').patchValue(this.businessAccount.business_zip);
    this.formData.get('country').patchValue(this.businessAccount.business_country);
  }

  private resetAddress() :void {
    if (this.shippingAddress) {
      this.formData.get('address').patchValue(this.shippingAddress.address);
      this.formData
        .get('addressLine2')
        .patchValue(this.shippingAddress.addressLine2);
      this.formData.get('state').patchValue(this.shippingAddress.state);
      this.formData.get('city').patchValue(this.shippingAddress.city);
      this.formData.get('postalCode').patchValue(this.shippingAddress.postalCode);
      this.formData.get('country').patchValue(this.shippingAddress.country);
    } else {
      this.formData.get('address').patchValue('');
      this.formData.get('addressLine2').patchValue('');
      this.formData.get('state').patchValue('');
      this.formData.get('city').patchValue('');
      this.formData.get('postalCode').patchValue('');
      this.formData.get('country').patchValue('');
    }
  }
}
